import type { JSX } from 'react';
import React, { lazy, Suspense } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { isProd } from './config/environment';
import { AudioPlayerProvider, AuthorizationProvider, UserProvider } from './providers';
import {
  createAppPersister,
  PERSIST_BUSTER,
  PERSIST_MAX_AGE,
  shouldDehydrateQuery,
} from './queries/persister';
import { queryClient } from './queries/queryClient';
import { Routes } from './routes';
import { cssVariablesResolver, theme } from './theme';

import '@mantine/core/styles.layer.css';

const router = createBrowserRouter(Routes);
const persister = createAppPersister();

// Dev-only: lazy-import devtools so they tree-shake out of the production bundle
// (a static import ships them even when render is gated). Renders nothing in prod.
const ReactQueryDevtools = isProd
  ? (): null => null
  : lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools }))
    );

function App(): JSX.Element {
  return (
    <UserProvider>
      <AuthorizationProvider>
        <MantineProvider
          theme={theme}
          cssVariablesResolver={cssVariablesResolver}
          defaultColorScheme="auto"
        >
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: PERSIST_MAX_AGE,
              buster: PERSIST_BUSTER,
              dehydrateOptions: { shouldDehydrateQuery },
            }}
          >
            <AudioPlayerProvider>
              <React.StrictMode>
                <RouterProvider router={router} />
              </React.StrictMode>
            </AudioPlayerProvider>
            {!isProd && (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </PersistQueryClientProvider>
        </MantineProvider>
      </AuthorizationProvider>
    </UserProvider>
  );
}

export default App;
